/**
 * Visual diff helpers for export parity audits.
 *
 * Two flavors, both gated on `window.__exportDiagnostics`:
 *
 *   - computeExportVisualDiff:
 *       Coarse whole-canvas comparison. Returns total mismatched pixel
 *       count + bbox. Confirms "something diverged" but not what.
 *
 *   - diffExportByElement:
 *       Walks each design element (with its known canvas-local bbox) and
 *       reports the diff ratio per element. Returns a list sorted by
 *       mismatch, so the next surgical fix targets the actual divergent
 *       renderer branch (text vs masked image vs transformed group).
 *
 * Both helpers re-render the live canvas via html2canvas at the same
 * scale as the export; the export pipeline's clip/text fix-ups are
 * skipped on the reference pass so the diff reflects html2canvas's own
 * rendering, not the fix-up layer.
 */

async function captureReference(liveCanvasEl, w, h) {
  if (typeof window === 'undefined' || !liveCanvasEl) return null;
  const html2canvas = (await import('html2canvas')).default;
  const originalTransform = liveCanvasEl.style.transform;
  liveCanvasEl.style.transform = 'none';
  try {
    let ref = await html2canvas(liveCanvasEl, {
      width:  Math.round(w / 2),
      height: Math.round(h / 2),
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });
    if (ref.width !== w || ref.height !== h) {
      const tmp = document.createElement('canvas');
      tmp.width = w; tmp.height = h;
      tmp.getContext('2d').drawImage(ref, 0, 0, w, h);
      ref = tmp;
    }
    return ref;
  } finally {
    liveCanvasEl.style.transform = originalTransform;
  }
}

export async function computeExportVisualDiff(liveCanvasEl, exportedCanvas, opts = {}) {
  if (!liveCanvasEl || !exportedCanvas) return null;
  const { tolerance = 8, sample = 1 } = opts;
  const w = exportedCanvas.width, h = exportedCanvas.height;
  if (!w || !h) return null;

  const reference = await captureReference(liveCanvasEl, w, h);
  if (!reference) return null;

  const refData = reference.getContext('2d').getImageData(0, 0, w, h).data;
  const expData = exportedCanvas.getContext('2d').getImageData(0, 0, w, h).data;

  let diffPixels = 0;
  let minX = w, minY = h, maxX = -1, maxY = -1;
  const step = Math.max(1, sample);
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      const idx = (y * w + x) * 4;
      const d = Math.abs(refData[idx] - expData[idx])
              + Math.abs(refData[idx + 1] - expData[idx + 1])
              + Math.abs(refData[idx + 2] - expData[idx + 2]);
      if (d > tolerance * 3) {
        diffPixels++;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  const totalSampled = Math.ceil(w / step) * Math.ceil(h / step);
  return {
    totalPixels: totalSampled,
    diffPixels,
    diffRatio: totalSampled ? diffPixels / totalSampled : 0,
    mismatchBBox: maxX < 0 ? null : { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 },
  };
}

/**
 * Per-element pixel-diff. `elements` is the design-data array (each with
 * id/type/x/y/width/height in canvas-local px). `canvasSize` is the
 * editor's authored canvas dimensions (px). The exported canvas is at
 * scale=2, so canvas-local px maps to export px via `* exportScale`.
 *
 * Returns elements sorted by diff ratio descending — top of the list is
 * where the renderer divergence lives.
 */
export async function diffExportByElement(liveCanvasEl, exportedCanvas, elements, canvasSize, opts = {}) {
  if (!liveCanvasEl || !exportedCanvas || !Array.isArray(elements) || !canvasSize) return null;
  const { tolerance = 12, sample = 2 } = opts;
  const w = exportedCanvas.width, h = exportedCanvas.height;
  if (!w || !h) return null;

  const reference = await captureReference(liveCanvasEl, w, h);
  if (!reference) return null;

  const refData = reference.getContext('2d').getImageData(0, 0, w, h).data;
  const expData = exportedCanvas.getContext('2d').getImageData(0, 0, w, h).data;

  // Exported is `scale=2` over canvasSize, so each canvas-local px == 2 export px.
  const sx = w / canvasSize.width;
  const sy = h / canvasSize.height;
  const step = Math.max(1, sample);
  const results = [];

  for (const el of elements) {
    if (!el || el.visible === false) continue;
    if (el.isMaskShape || el.isClipBase) continue;
    const ex = Math.max(0, Math.floor((el.x || 0) * sx));
    const ey = Math.max(0, Math.floor((el.y || 0) * sy));
    const ew = Math.min(w - ex, Math.ceil((el.width  || 0) * sx));
    const eh = Math.min(h - ey, Math.ceil((el.height || 0) * sy));
    if (ew <= 0 || eh <= 0) continue;

    let diff = 0, total = 0;
    for (let y = ey; y < ey + eh; y += step) {
      for (let x = ex; x < ex + ew; x += step) {
        const idx = (y * w + x) * 4;
        const d = Math.abs(refData[idx] - expData[idx])
                + Math.abs(refData[idx + 1] - expData[idx + 1])
                + Math.abs(refData[idx + 2] - expData[idx + 2]);
        total++;
        if (d > tolerance * 3) diff++;
      }
    }
    results.push({
      id: el.id,
      type: el.type,
      bbox: { x: ex, y: ey, w: ew, h: eh },
      diffPixels: diff,
      totalPixels: total,
      diffRatio: total ? diff / total : 0,
    });
  }

  results.sort((a, b) => b.diffRatio - a.diffRatio);
  return results;
}
